import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { JobRunsResponse } from "./types";

/** Default API base — services that mount jobruns at a different path can
 *  pass `apiBase` to JobsPage. Trailing slash NOT included. */
export const DEFAULT_API_BASE = "/api/admin/jobs";

async function getJSON<T>(url: string): Promise<T> {
  const r = await fetch(url, { credentials: "include" });
  if (!r.ok) throw new Error(`${url}: ${r.status}`);
  return r.json() as Promise<T>;
}

async function postJSON<T>(url: string): Promise<T> {
  const r = await fetch(url, { method: "POST", credentials: "include" });
  if (!r.ok) throw new Error(`${url}: ${r.status}`);
  return r.json() as Promise<T>;
}

async function deleteJSON<T>(url: string): Promise<T> {
  const r = await fetch(url, { method: "DELETE", credentials: "include" });
  if (!r.ok) throw new Error(`${url}: ${r.status}`);
  return r.json() as Promise<T>;
}

const runsKey = (apiBase: string, job: string | undefined, limit: number) =>
  ["job-runs", apiBase, job ?? "", limit] as const;

export function useJobRuns(opts: {
  apiBase?: string;
  job?: string;
  limit?: number;
  refetchMs?: number;
}) {
  const apiBase = opts.apiBase ?? DEFAULT_API_BASE;
  const limit = opts.limit ?? 200;
  return useQuery<JobRunsResponse>({
    queryKey: runsKey(apiBase, opts.job, limit),
    queryFn: () => {
      const u = new URL(`${apiBase}/runs`, window.location.origin);
      u.searchParams.set("limit", String(limit));
      if (opts.job) u.searchParams.set("job", opts.job);
      return getJSON<JobRunsResponse>(u.pathname + u.search);
    },
    refetchInterval: opts.refetchMs ?? 5_000,
  });
}

export function useCancelJobRun(apiBase: string = DEFAULT_API_BASE) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      postJSON<{ cancelled: boolean; marked: boolean }>(
        `${apiBase}/runs/${id}/cancel`,
      ),
    onSettled: () => qc.invalidateQueries({ queryKey: ["job-runs"] }),
  });
}

export function useDeleteJobRun(apiBase: string = DEFAULT_API_BASE) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      deleteJSON<{ deleted: boolean }>(`${apiBase}/runs/${id}`),
    onSettled: () => qc.invalidateQueries({ queryKey: ["job-runs"] }),
  });
}

export function useClearFinishedJobRuns(apiBase: string = DEFAULT_API_BASE) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      postJSON<{ deleted: number }>(`${apiBase}/runs/clear-finished`),
    onSettled: () => qc.invalidateQueries({ queryKey: ["job-runs"] }),
  });
}
